'use client'

import Link from 'next/link'
import React, { useEffect, useState } from 'react'

import { CONSENT_EVENT, CONSENT_OPEN_EVENT, readConsentCookie, storeConsent, type Consent } from './constants'
import { FacebookIcon } from './Icon'

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

function useConsent(initial: Consent) {
  const [consent, setConsent] = useState(initial)
  useEffect(() => {
    const onChange = (event: Event) => setConsent((event as CustomEvent<Consent>).detail)
    window.addEventListener(CONSENT_EVENT, onChange)
    return () => window.removeEventListener(CONSENT_EVENT, onChange)
  }, [])
  return consent
}

type BannerLabels = { title: string; necessaryOnly: string; acceptAll: string; learnMore: string }

export function CookieBanner({ initial, text, labels, policyHref }: { initial: Consent; text: string; labels: BannerLabels; policyHref: string }) {
  const [visible, setVisible] = useState(!initial.decided)
  useEffect(() => {
    const open = () => setVisible(true)
    window.addEventListener(CONSENT_OPEN_EVENT, open)
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, open)
  }, [])
  if (!visible) return null

  const choose = (all: boolean) => {
    storeConsent({ analytics: all, social: all })
    setVisible(false)
  }
  return (
    <section className="cookie-banner" role="region" aria-label={labels.title} data-testid="cookie-banner">
      <p>
        {text} <Link href={policyHref}>{labels.learnMore}</Link>
      </p>
      <div className="cookie-banner__actions">
        <button type="button" className="btn btn-secondary" onClick={() => choose(false)} data-testid="cookie-necessary">
          {labels.necessaryOnly}
        </button>
        <button type="button" className="btn btn-primary" onClick={() => choose(true)} data-testid="cookie-accept">
          {labels.acceptAll}
        </button>
      </div>
    </section>
  )
}

export function CookieSettingsButton({ label }: { label: string }) {
  return (
    <button type="button" className="link-button" onClick={() => window.dispatchEvent(new Event(CONSENT_OPEN_EVENT))}>
      {label}
    </button>
  )
}

// Google Analytics 4, injected only once analytics consent is given; withdrawing consent disables it.
export function Analytics({ gaId, initial }: { gaId: string; initial: Consent }) {
  const consent = useConsent(initial)
  useEffect(() => {
    const disableKey = `ga-disable-${gaId}`
    ;(window as unknown as Record<string, boolean>)[disableKey] = !consent.analytics
    if (!consent.analytics || window.gtag) return
    window.dataLayer = window.dataLayer || []
    window.gtag = function gtag() {
      // gtag.js expects the `arguments` object itself, not an array.
      window.dataLayer!.push(arguments)
    }
    window.gtag('js', new Date())
    window.gtag('config', gaId, { anonymize_ip: true })
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`
    document.head.appendChild(script)
  }, [consent.analytics, gaId])
  return null
}

type FacebookLabels = { title: string; open: string; consent: string; show: string }

// The Facebook page plugin sets third-party cookies: iframe only after "social" consent, a link card otherwise.
export function FacebookEmbed({ pageUrl, initial, labels }: { pageUrl: string; initial: Consent; labels: FacebookLabels }) {
  const consent = useConsent(initial)
  if (consent.social) {
    const src = `https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(pageUrl)}&tabs=timeline&width=500&height=560&small_header=true&adapt_container_width=true&hide_cover=false&show_facepile=false`
    return <iframe className="fb-embed" title={labels.title} src={src} width={500} height={560} loading="lazy" allow="encrypted-media; clipboard-write" data-testid="facebook-embed" />
  }
  return (
    <div className="fb-card" data-testid="facebook-link-card">
      <p className="fb-card__head">
        <span className="fb-card__icon">
          <FacebookIcon size={28} />
        </span>
        <strong>{pageUrl.replace(/^https:\/\/(www\.)?/, '')}</strong>
      </p>
      <a className="btn btn-primary" href={pageUrl} target="_blank" rel="noopener noreferrer">
        {labels.open}
      </a>
      <p className="fb-card__consent">{labels.consent}</p>
      <button type="button" className="link-button" onClick={() => storeConsent({ analytics: readConsentCookie().analytics, social: true })} data-testid="facebook-consent">
        {labels.show}
      </button>
    </div>
  )
}
